import { useBase } from "@/contexts/BaseContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Globe } from "lucide-react";

/**
 * Header base switcher. All-access users (master/admin/executive) can pick
 * a specific base or "All Bases"; base-scoped users only see their own
 * base(s). Hidden entirely when the user has a single base and no
 * all-access (nothing to switch).
 *
 * The leading glyph is the active base's COUNTRY FLAG (e.g. 🇿🇦 / 🇬🇭);
 * when "All Bases" is selected we fall back to a globe icon.
 *
 * `variant="mobile"` renders a more compact trigger for the mobile header.
 */
export function BaseSwitcher({ variant = "desktop" }) {
  const { bases, allAccess, activeBase, activeBaseMeta, changeBase, showSwitcher } = useBase();
  if (!showSwitcher) return null;

  const value = activeBase || "all";
  const compact = variant === "mobile";
  const showGlobe = value === "all";
  const leadFlag = showGlobe ? null : (activeBaseMeta?.flag || bases.find((b) => b.id === value)?.flag);

  return (
    <Select value={value} onValueChange={changeBase}>
      <SelectTrigger
        data-testid="base-switcher-trigger"
        aria-label="Switch base"
        className={
          compact
            ? "h-8 w-auto min-w-0 gap-1 border-[hsl(var(--ap-brass)/0.4)] bg-[hsl(var(--ap-brass)/0.08)] px-2 text-xs"
            : "h-9 w-full gap-2 border-[hsl(var(--ap-brass)/0.4)] bg-[hsl(var(--ap-brass)/0.08)] text-sm"
        }
      >
        {showGlobe
          ? <Globe className="h-4 w-4 shrink-0 text-[hsl(var(--ap-saddle))]" />
          : <span className="text-base leading-none shrink-0" aria-hidden="true">{leadFlag || "🏳️"}</span>}
        <SelectValue placeholder="Select base" />
      </SelectTrigger>
      <SelectContent data-testid="base-switcher-content">
        {allAccess && (
          <SelectItem value="all" data-testid="base-option-all">
            <span className="inline-flex items-center gap-2"><Globe className="h-3.5 w-3.5" /> All Bases</span>
          </SelectItem>
        )}
        {bases.map((b) => (
          <SelectItem key={b.id} value={b.id} data-testid={`base-option-${b.code || b.id}`}>
            <span className="inline-flex items-center gap-2">
              <span aria-hidden="true">{b.flag || "🏳️"}</span>
              {b.name}{b.code ? ` (${b.code})` : ""}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
